import React, { useState, useEffect, forwardRef } from "react";
import { Input } from "@/components/ui/input";
import { Link } from "lucide-react";

interface UrlInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
}

export const UrlInput = forwardRef<HTMLInputElement, UrlInputProps>(
  (
    {
      value,
      onChange,
      onBlur,
      placeholder = "example.com or https://example.com",
      className = "",
      ...props
    },
    ref,
  ) => {
    const [localValue, setLocalValue] = useState(value || "");

    useEffect(() => {
      setLocalValue(value || "");
    }, [value]);

    const formatUrl = (input: string): string => {
      if (!input) return "";

      input = input.trim();

      if (/^https?:\/\//i.test(input)) {
        return input;
      }

      if (input.includes(".") && !input.includes("://")) {
        return `https://${input}`;
      }

      return input;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      setLocalValue(input);

      if (onChange) {
        onChange(e);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const formattedUrl = formatUrl(localValue);

      if (formattedUrl !== localValue) {
        setLocalValue(formattedUrl);

        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: formattedUrl,
          },
        } as React.ChangeEvent<HTMLInputElement>;

        if (onChange) {
          onChange(syntheticEvent);
        }
      }

      if (onBlur) {
        onBlur(e);
      }
    };

    return (
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10">
          <Link className="h-4 w-4" />
        </div>
        <Input
          ref={ref}
          type="text"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`pl-10 ${className}`}
          {...props}
        />
      </div>
    );
  },
);

UrlInput.displayName = "UrlInput";
